import * as authStore from '@store/auth/auth'
import * as accountStore from '@store/account/account'
import * as filtersStore from '@store/filters/filters'

import OAuthConfig from '@app/config'
import axios from 'axios'

import { addSeconds } from 'date-fns'
import Logger from './Logger'
import pkceChallenge from 'pkce-challenge'

const loginUrl = window.location.origin

const login = (mode: AuthMode): void => {
  const codes = pkceChallenge(128)
  authStore.actions.setCodeVerifier(codes.code_verifier)
  const { authorizationEndpoint, clientId, redirectUrl, scopes } = OAuthConfig.AUTH_CONFIG[mode]
  const axiosParam = new URLSearchParams()
  axiosParam.append('client_id', clientId)
  axiosParam.append('nonce', '8s9jcvj4oj')
  axiosParam.append('redirect_uri', loginUrl + redirectUrl)
  axiosParam.append('response_type', 'code')
  axiosParam.append('scope', scopes.join(' '))
  axiosParam.append('state', 'mystate')
  axiosParam.append('code_challenge', codes.code_challenge)
  axiosParam.append('code_challenge_method', 'S256')

  window.open(`${authorizationEndpoint}?${axiosParam.toString()}`, '_self')
}

const exchangeCode = (
  mode: AuthMode,
  code: string,
  getUserInfo: (tokens: OauthCredentials | undefined) => void
): Promise<void> => {
  const { tokenEndpoint, clientId, redirectUrl } = OAuthConfig.AUTH_CONFIG[mode]
  const code_verifier = authStore.store.getState().code_verifier

  if (!code_verifier) {
    Logger.info("CAN'T CONNECT : CODE_VERIFIER MISSING IN APP")
    return Promise.reject()
  }

  const axiosParam = new URLSearchParams()
  axiosParam.append('content_type', 'application/json')
  axiosParam.append('grant_type', 'authorization_code')
  axiosParam.append('client_id', clientId)
  axiosParam.append('code', code)
  axiosParam.append('code_verifier', code_verifier)
  axiosParam.append('redirect_uri', loginUrl + redirectUrl)

  return axios
    .post(tokenEndpoint, axiosParam)
    .then((tokens) => {
      const { access_token, id_token, expires_in, refresh_token } = tokens.data
      const storeTokens = {
        auth_mode: mode,
        access_token: access_token,
        id_token: id_token,
        expire_at: addSeconds(new Date(), expires_in).getTime(),
        refresh_token: refresh_token,
        lastAuthentication: new Date().getTime(),
      }
      authStore.actions.setTokens(storeTokens)
      // Appel du /me ici pour s'assurer d'utiliser les bons tokens
      getUserInfo(storeTokens)
    })
    .catch((err) => {
      authStore.actions.resetCodeVerifier()
      Logger.info('ERROR EXCHANGING CODE : ', err)
    })
}

const refreshTokens = () => {
  const storeTokens = authStore.store.getState().OAuth
  if (!storeTokens) {
    Logger.info('ERROR REFRESHING TOKENS : NO REFRESH TOKEN')
    return Promise.reject()
  }
  const { tokenEndpoint, clientId } = OAuthConfig.AUTH_CONFIG[storeTokens.auth_mode]
  const axiosParam = new URLSearchParams()
  axiosParam.append('content_type', 'application/json')
  axiosParam.append('grant_type', 'refresh_token')
  axiosParam.append('refresh_token', storeTokens.refresh_token)
  axiosParam.append('client_id', clientId)

  return axios
    .post(tokenEndpoint, axiosParam)
    .then((tokens) => {
      const { access_token, id_token, expires_in, refresh_token } = tokens.data
      authStore.actions.setTokens({
        auth_mode: storeTokens.auth_mode,
        access_token: access_token,
        id_token: id_token,
        expire_at: addSeconds(new Date(), expires_in).getTime(),
        refresh_token: refresh_token,
        lastAuthentication: new Date().getTime(),
      })
    })
    .catch((err) => Logger.info('ERROR REFRESHING TOKENS : ', err))
}

const logout = () => {
  const OAuthState = authStore.store.getState().OAuth
  if (!OAuthState) {
    return
  }
  const OAuthMode = OAuthState.auth_mode
  const { logoutEndpoint, logoutRedirectUrl } = OAuthConfig.AUTH_CONFIG[OAuthMode]
  const axiosParam = new URLSearchParams()
  axiosParam.append('id_token_hint', OAuthState.id_token)
  const postLogoutRedirecUri = `&post_logout_redirect_uri=${loginUrl + logoutRedirectUrl}`
  document.location.href = `${logoutEndpoint}?${axiosParam.toString()}${postLogoutRedirecUri}`
}

const reset = () => {
  authStore.actions.resetTokens()
  accountStore.actions.reset()
  filtersStore.actions.reset()
}

const oauth = {
  login,
  logout,
  reset,
  exchangeCode,
  refreshTokens,
}

export default oauth
